import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Handshake, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { name: "", company_name: "", mobile: "", email: "" };

export default function SponsorDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setForm(EMPTY); setDone(false); setLoading(false); }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name && form.company_name && form.mobile && form.email;

  const submit = async () => {
    if (!valid) { toast.error("Please fill all required fields."); return; }
    setLoading(true);
    try {
      await api.post("/sponsors", form);
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sponsor-dialog" className="bg-[#121212] border-border max-w-md">
        {done ? (
          <div data-testid="sponsor-success" className="flex flex-col items-center text-center py-8 px-2 animate-fade-up">
            <div className="h-20 w-20 rounded-full bg-[#FF4500]/15 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-11 w-11 text-[#FF4500]" strokeWidth={2.2} />
            </div>
            <h3 className="font-display text-3xl font-800 uppercase tracking-tight text-white">Thank You!</h3>
            <p className="mt-3 text-[#A1A1AA] max-w-sm">
              Thanks for your interest in sponsoring Meraki Moto Mania. Our team will contact you shortly.
            </p>
            <Button data-testid="sponsor-close-button" onClick={() => onOpenChange(false)} className="mt-8 rounded-full bg-[#FF4500] hover:bg-[#FF5722] text-white px-10 h-12 text-base font-semibold transition-colors">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-800 uppercase tracking-tight flex items-center gap-2">
                <Handshake className="h-6 w-6 text-[#FF4500]" /> Be Our Sponsor
              </DialogTitle>
              <p className="text-sm text-[#A1A1AA]">Partner with India's most electric moto gathering. Drop your details.</p>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <Field label="Name" required>
                <Input data-testid="sponsor-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" />
              </Field>
              <Field label="Company Name" required>
                <Input data-testid="sponsor-company-input" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Your company / brand" />
              </Field>
              <Field label="Mobile" required>
                <Input data-testid="sponsor-mobile-input" type="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 98XXXXXXXX" />
              </Field>
              <Field label="Email" required>
                <Input data-testid="sponsor-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
              </Field>
              <Button
                data-testid="sponsor-submit-button"
                onClick={submit}
                disabled={loading}
                className="w-full h-14 rounded-full bg-[#FF4500] hover:bg-[#FF5722] text-white text-lg font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Enquiry"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">
        {label} {required && <span className="text-[#FF4500]">*</span>}
      </Label>
      {children}
    </div>
  );
}
